/**
 * WebSocket client for the blind relay.
 *
 * Responsibilities: authenticate, auto-reconnect with backoff, hand inbound
 * `deliver` frames to the app, and expose `send` / `ack`. It never inspects
 * ciphertext. Delivery ACKs are the caller's responsibility — call `ack(id)`
 * only after the envelope has been durably persisted.
 */
import type {
  DeliverFrame,
  ErrorFrame,
  IncomingFrame,
  PresenceFrame,
  SendFrame,
  ServerFrame,
  WelcomeFrame,
} from "@truewave/protocol";
import { config } from "../lib/config.js";

export type ChannelStatus = "idle" | "connecting" | "online" | "offline";

export interface ChannelHandlers {
  onStatus?: (s: ChannelStatus) => void;
  onWelcome?: (f: WelcomeFrame) => void;
  onDrained?: () => void;
  onDeliver: (f: DeliverFrame) => void | Promise<void>;
  onSent?: (id: string, live: boolean, pushable: boolean) => void;
  onError?: (f: ErrorFrame) => void;
  onPresence?: (f: PresenceFrame) => void;
  /** Locked mode only: something arrived that deserves a ring or chime. */
  onIncoming?: (f: IncomingFrame) => void;
}

interface Creds {
  userId: string;
  token: string;
}

const MAX_BACKOFF_MS = 30_000;
/** How long a liveness ping may go unanswered before the socket is presumed dead. */
const PONG_TIMEOUT_MS = 4_000;

export class Channel {
  private ws: WebSocket | null = null;
  private creds: Creds | null = null;
  private handlers: ChannelHandlers | null = null;
  private status: ChannelStatus = "idle";
  private backoff = 1000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private outQueue: SendFrame[] = [];
  private closedByUs = false;
  // Session state the relay forgets on disconnect, re-sent after every welcome.
  private active: boolean | null = null;
  private watchIds: string[] | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  private mode: "full" | "locked" = "full";

  /**
   * `mode: "locked"` opens an alerts-only connection for the app's lock
   * screen: the relay delivers nothing to it, only `incoming` alerts.
   */
  connect(creds: Creds, handlers: ChannelHandlers, mode: "full" | "locked" = "full"): void {
    this.creds = creds;
    this.handlers = handlers;
    this.mode = mode;
    this.closedByUs = false;
    this.open();
  }

  disconnect(): void {
    this.closedByUs = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    if (this.pongTimer) clearTimeout(this.pongTimer);
    this.pongTimer = null;
    this.ws?.close(1000, "client disconnect");
    this.ws = null;
    this.active = null;
    this.watchIds = null;
    this.setStatus("idle");
  }

  get connected(): boolean {
    return this.status === "online";
  }

  /** Queue a send; flushed immediately if online, otherwise on reconnect. */
  send(frame: SendFrame): void {
    this.outQueue.push(frame);
    this.flush();
  }

  ack(id: string): void {
    this.raw({ t: "ack", id });
  }

  /** Locked mode: confirm an `incoming` alert was shown, so no push is sent. */
  seen(id: string): void {
    this.raw({ t: "seen", id });
  }

  /** Tell the relay whether the app is on screen (drives "online" for contacts). */
  setActive(active: boolean): void {
    if (this.active === active) return;
    this.active = active;
    // Before `welcome` the relay only accepts an auth frame; welcome re-sends it.
    if (this.status === "online") this.raw({ t: "active", active });
  }

  /** Follow these users' online status (replaces the previous list). */
  watch(userIds: string[]): void {
    this.watchIds = userIds;
    if (this.status === "online") this.raw({ t: "watch", userIds });
  }

  ping(): void {
    this.raw({ t: "ping", ts: Date.now() });
  }

  /**
   * The app just came back to the screen (phone unlocked, app reopened from a
   * notification). Make sure we're really connected, right now:
   *  - offline → reconnect immediately, instead of waiting out a backoff that
   *    grows to 30s while the phone sleeps (which used to eat half of a
   *    60-second incoming call before anything arrived);
   *  - "online" → prove it with a ping. iOS often leaves a suspended app's
   *    socket looking open when the connection is long gone; if no pong comes
   *    back quickly, drop it and reconnect.
   */
  resume(): void {
    if (this.closedByUs || !this.creds) return;
    if (this.status !== "online" || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
      if (this.status === "connecting" && this.ws?.readyState === WebSocket.CONNECTING) return;
      this.reconnectNow();
      return;
    }
    if (this.pongTimer) return;
    this.pongTimer = setTimeout(() => {
      this.pongTimer = null;
      this.reconnectNow();
    }, PONG_TIMEOUT_MS);
    this.ping();
  }

  private reconnectNow(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    // Belongs to the socket being replaced; left armed it would kill the new one.
    if (this.pongTimer) clearTimeout(this.pongTimer);
    this.pongTimer = null;
    this.backoff = 1000;
    const old = this.ws;
    this.ws = null;
    if (old) {
      // Its close handler sees it's been replaced and won't schedule a
      // second, delayed reconnect.
      try {
        old.close();
      } catch {
        /* already dead */
      }
    }
    this.open();
  }

  // --- internals ---------------------------------------------------------

  private setStatus(s: ChannelStatus): void {
    if (s === this.status) return;
    this.status = s;
    this.handlers?.onStatus?.(s);
  }

  private open(): void {
    if (!this.creds) return;
    if (this.pongTimer) clearTimeout(this.pongTimer);
    this.pongTimer = null;
    this.setStatus("connecting");
    const ws = new WebSocket(config.wsUrl);
    this.ws = ws;

    ws.addEventListener("open", () => {
      ws.send(
        JSON.stringify({ t: "auth", v: 1, userId: this.creds!.userId, token: this.creds!.token, mode: this.mode }),
      );
    });

    ws.addEventListener("message", (ev) => {
      let frame: ServerFrame;
      try {
        frame = JSON.parse(typeof ev.data === "string" ? ev.data : "") as ServerFrame;
      } catch {
        return;
      }
      this.handle(frame);
    });

    ws.addEventListener("close", () => {
      if (this.ws !== ws) return; // replaced by reconnectNow()
      this.ws = null;
      if (this.closedByUs) return;
      this.setStatus("offline");
      this.scheduleReconnect();
    });

    ws.addEventListener("error", () => {
      ws.close();
    });

    ws.addEventListener("message", () => {
      // Any traffic proves the socket is alive.
      if (this.pongTimer && this.ws === ws) {
        clearTimeout(this.pongTimer);
        this.pongTimer = null;
      }
    });
  }

  private handle(frame: ServerFrame): void {
    switch (frame.t) {
      case "welcome":
        this.backoff = 1000;
        this.setStatus("online");
        this.handlers?.onWelcome?.(frame);
        if (this.active !== null) this.raw({ t: "active", active: this.active });
        if (this.watchIds !== null) this.raw({ t: "watch", userIds: this.watchIds });
        this.flush();
        break;
      case "drained":
        this.handlers?.onDrained?.();
        break;
      case "deliver":
        void this.handlers?.onDeliver(frame);
        break;
      case "sent":
        this.outQueue = this.outQueue.filter((f) => f.id !== frame.id);
        this.handlers?.onSent?.(frame.id, frame.live, frame.pushable ?? false);
        break;
      case "error":
        this.handlers?.onError?.(frame);
        if (frame.fatal) this.disconnect();
        break;
      case "presence":
        this.handlers?.onPresence?.(frame);
        break;
      case "incoming":
        this.handlers?.onIncoming?.(frame);
        break;
      case "pong":
        break;
      case "ack":
        break;
    }
  }

  private flush(): void {
    if (this.status !== "online" || !this.ws) return;
    for (const frame of this.outQueue) this.raw(frame);
  }

  private raw(frame: object): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(frame));
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || this.closedByUs) return;
    const wait = this.backoff + Math.random() * 500;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.backoff = Math.min(MAX_BACKOFF_MS, this.backoff * 2);
      this.open();
    }, wait);
  }
}

export const channel = new Channel();
